import React, { memo } from 'react';
import isEqual from 'lodash.isequal';
import { differenceInMinutes } from 'date-fns';
import { useTranslation } from 'react-i18next';

// Styled Components
import {
  Container,
  Title,
  ColumnsContainer,
  Column,
  ContainerButtons,
  GeneralContainer,
  MapContainer,
  BasicInfo,
  SecondGeneralColumn,
  FirstGeneralColumn,
  ContentContainer,
  StyledDefinition,
  PersonDefinition,
  TabContainer,
  StyledColumnsContainer,
  BreakdownRow,
  TitleTableContainer,
  HeaderContainer,
  SpecialTagStyled,
  PergaminoList,
} from './styles';

import { variant as SpecialTagVariant } from '../SpecialTag/types';

// Media
import { useIsLandscapeTablet } from '../../../hooks/useMediaBreakPoints';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';

// Components
import InputRoid from '../../inputs/InputRoid';
import { Definition } from '../Definition';
import { MainButton } from '../../inputs/buttons/MainButton';
import { MaterialsTable } from '../tables/summaryTables/MaterialsTable';
import { AuxiliaryMachineryTable } from '../tables/summaryTables/AuxiliaryMachineryTable';
import { BudgetItemsTable } from '../tables/summaryTables/BudgetItemsTable';
import { OperatorsTable } from '../tables/summaryTables/OperatorsTable';
import { GridOfPhotos } from '../../multimedia/GridOfPhotos';
import { mapToGridImages } from './mapToGridImages';
import { ImageData } from '../ResumeLoadImages/types';
import { MapComponent } from '../Map';
import { AnimalsTable } from '../tables/summaryTables/AnimalsTable';
import { VehiclesTable } from '../tables/summaryTables/VehiclesTable';
import { WorkVehiclesTable } from '../tables/summaryTables/WorkVehiclesTable';

// Types
import {
  ContentCardResumeProps,
  marginArrow,
  directionArrow,
  dataTabs,
} from './types';
import { UNSPECIFIC } from '../../../types/utils';

import { Typography } from '../Typography';
import { InputTextArea } from '../../inputs/InputTextArea';
import { ExternalCompaniesTable } from '../tables/summaryTables/ExternalCompanies';
const MemorizedMap = memo(MapComponent, isEqual);
// Map OrderState to SpecialTag variant
const getStatusVariant = (status?: string): SpecialTagVariant | null => {
  if (!status) return null;
  const mapping: Record<string, SpecialTagVariant> = {
    open: SpecialTagVariant.Open,
    validated: SpecialTagVariant.Validated,
    'pending-certification': SpecialTagVariant.PendingCertification, // Tarea #7
    annulled: SpecialTagVariant.Annulled,
    end: SpecialTagVariant.End,
    'pending-validation': SpecialTagVariant.PendingValidation,
    'pending-at-validation': SpecialTagVariant.PendingATValidation, // T66
    'pending-work': SpecialTagVariant.PendingWork,
    'work-in-progress': SpecialTagVariant.WorkInProgress,
    managed: SpecialTagVariant.Managed,
    partial: SpecialTagVariant.Partial,
  };
  return mapping[status] || null;
};

export default function ActionContentCardResume({
  isActionEditedBoolean,
  routes,
  roid,
  actionStatus,
  date,
  hour,
  capitol,
  subCapitol,
  concession,
  road,
  pkInit,
  pkEnd,
  track,
  city,
  roadDirections,
  direction,
  margin,
  lane,
  leftButton,
  rightButton,
  description,
  descriptionValidated,
  operatorDescription,
  creatorName,
  registeredByName,
  notes,
  isMaterialTableDisplayed,
  isAnimalsTableDisplayed,
  asset,
  assetSwitch,
  activity,
  isMachineryTableDisplayed,
  isBudgetItemsTableDisplayed,
  materialsData = [],
  animalsData = [],
  machineryData = [],
  budgetItemsData = [],
  operators = [],
  vehicles = [],
  startDate,
  startHour,
  endDate,
  endHour,
  registerInitDate,
  registerEndDate,
  basicSchedule = false,
  arrivalDate,
  arrivalHour,
  totalHours,
  totalOperators,
  isSimultaneous,
  element,
  laneCutting,
  sketch,
  images,
  works,
  mapCenter,
  mapPoint,
  path,
  geolocation,
  onEditAction,
  onContinueAction,
  validateButton,
  showValidateButton,
  isValidating,
  onValidateAction,
  pendingCertificationButton,
  showPendingCertificationButton,
  isMarkingPendingCertification,
  onPendingCertificationAction,
  onStatusClick,
  ...props
}: ContentCardResumeProps): JSX.Element {
  const { t } = useTranslation('forms');
  const isLandscape = useIsLandscapeTablet();
  // Feature flag para mostrar/ocultar campo margen
  const showMargin = useFeatureFlag('showMargin', true);

  // ✅ NUEVO: Separar vehículos por usage
  const transportVehicles = vehicles.filter(
    (v: any) => v.usage === 'TRANSPORT'
  );
  const workVehicles = vehicles
    .filter((v: any) => v.usage === 'CONSTRUCTION')
    .map((v: any) => ({
      plate: v.mat || v.plate || '',
      model: v.model || '',
      category: v.category || '',
      driver: v.driver || '-',
      ownership: (v.ownership || 'INTERNAL') as 'INTERNAL' | 'EXTERNAL',
    }));

  // Desglose de horas-persona internos/externos
  const parseTimeToMinutes = (hhmm: string): number => {
    const [h, m] = (hhmm || '00:00').split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  };
  const formatMinutesToHHMM = (min: number): string => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };
  // Jornada = diferencia entre hora inicio y hora fin (NO totalHours que ya es horas-persona).
  // El trabajo lo definen inicio y fin: si NO hay hora final válida, la jornada es 0.
  // (Antes, sin hora final, parseTimeToMinutes('-')=0 → restaba contra 00:00 e inflaba
  //  las H.P. con la hora de inicio. La hora de llegada NUNCA entra en este cálculo.)
  const hasValidEndHour =
    !!endHour && endHour !== '-' && /^\d{1,2}:\d{2}$/.test(endHour);
  // Jornada con timestamps COMPLETOS (incluye días) si tenemos las fechas ISO
  // — MISMO criterio que el wizard OperatorsAndVehicles.calculateHourDifference
  // (differenceInMinutes, segundos normalizados, ≥0 else 0). Antes solo restaba
  // HH:MM e ignoraba el día (una jornada cross-day de 24:41 se mostraba 00:41).
  const fullShiftMinutes = ((): number | null => {
    if (!registerInitDate || !registerEndDate) return null;
    const s = new Date(registerInitDate);
    const e = new Date(registerEndDate);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
    s.setSeconds(0, 0);
    e.setSeconds(0, 0);
    const diff = differenceInMinutes(e, s);
    return Number.isNaN(diff) || diff < 0 ? 0 : diff;
  })();
  const rawShiftMinutes =
    fullShiftMinutes !== null
      ? fullShiftMinutes
      : hasValidEndHour
        ? Math.abs(parseTimeToMinutes(endHour) - parseTimeToMinutes(startHour))
        : 0;
  const internalCount = operators.length;
  const internalMinutes = internalCount * rawShiftMinutes;
  const externalBreakdown = (works || []).map((w) => ({
    company: w.company,
    count: Number(w.operatorsNumber),
    minutes: Number(w.operatorsNumber) * rawShiftMinutes,
  }));
  const externalMinutes = externalBreakdown.reduce((s, e) => s + e.minutes, 0);
  const grandTotalMinutes = internalMinutes + externalMinutes;
  const hasBothTypes = internalCount > 0 && externalBreakdown.length > 0;

  // 🔍 Logs para debugging
  console.log('🚗 ActionContentCardResume - Total vehicles:', vehicles.length);
  console.log(
    '🚚 Transport vehicles:',
    transportVehicles.length,
    transportVehicles
  );
  console.log(
    '🚛 Work vehicles (CONSTRUCTION):',
    workVehicles.length,
    workVehicles
  );

  const materialsTableWithTab = isMaterialTableDisplayed && {
    label: t('tabs.materials'),
    content: (
      <TabContainer>
        <TitleTableContainer>
          <Typography size={isLandscape ? 'M' : 'L'} variant="semiBold">
            {t('tabs.materials')}
          </Typography>
          <MaterialsTable data={materialsData} />
        </TitleTableContainer>
      </TabContainer>
    ),
  };
  // ✅ Mostrar tab si hay maquinaria auxiliar O vehículos de obra
  const machineryTableWithTab = (isMachineryTableDisplayed ||
    workVehicles.length > 0) && {
    label: t('tabs.machinery'),
    content: (
      <TabContainer>
        {/* Maquinaria Auxiliar */}
        {isMachineryTableDisplayed && (
          <TitleTableContainer>
            <Typography size={isLandscape ? 'M' : 'L'} variant="semiBold">
              {t('tabs.machinery')}
            </Typography>
            <AuxiliaryMachineryTable data={machineryData} />
          </TitleTableContainer>
        )}
        {/* ✅ Vehículos de obra (CONSTRUCTION) se muestran SIEMPRE en Maquinaria Aux. */}
        {workVehicles.length > 0 && (
          <TitleTableContainer>
            <Typography size={isLandscape ? 'M' : 'L'} variant="semiBold">
              {t('tabs.workVehicles', 'Vehículos de Obra')}
            </Typography>
            <WorkVehiclesTable data={workVehicles} />
          </TitleTableContainer>
        )}
      </TabContainer>
    ),
  };

  const animalsTableWithTab = isAnimalsTableDisplayed && {
    label: t('tabs.animals'),
    content: (
      <TabContainer>
        <TitleTableContainer>
          <Typography size={isLandscape ? 'M' : 'L'} variant="semiBold">
            {t('tabs.animals')}
          </Typography>
          <AnimalsTable data={animalsData} />
        </TitleTableContainer>
      </TabContainer>
    ),
  };

  // Only show budget items tab if there are actual items (not just an empty array)
  const budgetItemsTableWithTab = isBudgetItemsTableDisplayed && budgetItemsData && budgetItemsData.length > 0 && {
    label: t('tabs.budgetItems'),
    content: (
      <TabContainer>
        <TitleTableContainer>
          <Typography size={isLandscape ? 'M' : 'L'} variant="semiBold">
            {t('tabs.budgetItems')}
          </Typography>
          <BudgetItemsTable data={budgetItemsData} />
        </TitleTableContainer>
      </TabContainer>
    ),
  };

  const hasAnyOperatorData = internalCount > 0 || externalBreakdown.length > 0 || transportVehicles.length > 0;

  const dataTabs: dataTabs[] = ([
    // Horario de la Actuació bàsica (plan 2026-10-08): sin tripulación, la
    // pestaña de operarios no sale y con ella desaparecían las fechas. Inicio,
    // fin y duración del hueco del tramo.
    basicSchedule && ({
      label: t('actionEdition.basicSchedule'),
      content: (
        <TabContainer data-testid="summary-basic-schedule">
          <StyledColumnsContainer>
            <Definition title={t('workOrder.startDate')} value={startDate} />
            <Definition title={t('workOrder.startHour')} value={startHour} />
            {/* Hora de llegada (12-oct): en la misma fila, entre inicio y fin
                (el orden de «Datos Act» y de Operarios); solo si alguien la
                puso (no se calcula). */}
            {arrivalDate && (
              <>
                <Definition
                  title={t('workOrder.arrivalDate')}
                  value={arrivalDate}
                />
                <Definition
                  title={t('workOrder.arrivalHour')}
                  value={arrivalHour ?? ''}
                />
              </>
            )}
            <Definition title={t('workOrder.endDate')} value={endDate} />
            <Definition title={t('workOrder.endHour')} value={endHour} />
            <Definition
              title={t('workOrder.totalHours')}
              value={
                fullShiftMinutes !== null
                  ? formatMinutesToHHMM(fullShiftMinutes)
                  : '-'
              }
            />
          </StyledColumnsContainer>
          {/* Sin la frase de cómo se calcula (12-oct): en el resumen sobra, y
              aquí el bloque no tiene título al que colgar el «i». */}
        </TabContainer>
      ),
    }),
    hasAnyOperatorData && ({
      label: t('tabs.operatorsAndVehicles'),
      content: (
        <TabContainer>
          <TitleTableContainer>
            {operators && operators.length > 0 && (
              <>
                <Typography size={isLandscape ? 'M' : 'L'} variant="semiBold">
                  {t('tabs.operators')}
                </Typography>
                <OperatorsTable data={operators} />
              </>
            )}
            {works && works.length > 0 && (
              <TitleTableContainer>
                <Typography size={isLandscape ? 'M' : 'L'} variant="semiBold">
                  {t('tabs.externalCompanies')}
                </Typography>
                <ExternalCompaniesTable data={works} />
              </TitleTableContainer>
            )}
            {/* Solo mostrar fechas/desglose/totales si hay operarios o empresas */}
            {(internalCount > 0 || externalBreakdown.length > 0) && (
              <>
                {/* Fechas y horas — fila horizontal */}
                <StyledColumnsContainer style={{ marginTop: '1.5rem' }}>
                  <Definition
                    title={t('workOrder.startDate')}
                    value={startDate}
                  />
                  <Definition
                    title={t('workOrder.startHour')}
                    value={startHour}
                  />
                  {/* Hora de llegada (opcional): solo si la actuación la tiene.
                      Entre inicio y fin, el orden de Operarios (12-oct). */}
                  {arrivalDate && (
                    <>
                      <Definition
                        title={t('workOrder.arrivalDate')}
                        value={arrivalDate}
                      />
                      <Definition
                        title={t('workOrder.arrivalHour')}
                        value={arrivalHour ?? ''}
                      />
                    </>
                  )}
                  <Definition title={t('workOrder.endDate')} value={endDate} />
                  <Definition title={t('workOrder.endHour')} value={endHour} />
                </StyledColumnsContainer>
                {/* Desglose H.P. — wrap siempre para que no se corten */}
                <BreakdownRow>
                  {internalCount > 0 && (
                    <Definition
                      title={`${t('operatorsAndVehicles.internalOperatorsHours', 'Op. internos')} (${internalCount}) ${t('operatorsAndVehicles.personHours', 'H.P.')}`}
                      value={formatMinutesToHHMM(internalMinutes)}
                    />
                  )}
                  {externalBreakdown.map((ext, i) => (
                    <Definition
                      key={i}
                      title={`${ext.company} (${ext.count} op.) ${t('operatorsAndVehicles.personHours', 'H.P.')}`}
                      value={formatMinutesToHHMM(ext.minutes)}
                    />
                  ))}
                </BreakdownRow>
                {/* Totales — en negrita */}
                <BreakdownRow>
                  {hasBothTypes && (
                    <>
                      <StyledDefinition
                        title={t('operatorsAndVehicles.totalInternalHours', 'Total H. internas')}
                        value={formatMinutesToHHMM(internalMinutes)}
                      />
                      <StyledDefinition
                        title={t('operatorsAndVehicles.totalExternalHours', 'Total H. externas')}
                        value={formatMinutesToHHMM(externalMinutes)}
                      />
                    </>
                  )}
                  <StyledDefinition
                    title={
                      isSimultaneous
                        ? `${t('operatorsAndVehicles.totalPersonHours', 'Total H.P.')} · ${t('operatorsAndVehicles.simultaneous.label')}`
                        : t('operatorsAndVehicles.totalPersonHours', 'Total H.P.')
                    }
                    value={formatMinutesToHHMM(
                      isSimultaneous ? 0 : grandTotalMinutes,
                    )}
                  />
                  <StyledDefinition
                    title={t('workOrder.totalOperators')}
                    value={totalOperators}
                  />
                </BreakdownRow>
              </>
            )}
          </TitleTableContainer>
          {/* Vehículos de TRANSPORTE (si hay operarios internos) */}
          {transportVehicles.length > 0 && (
            <TitleTableContainer>
              <Typography size={isLandscape ? 'M' : 'L'} variant="semiBold">
                {t('tabs.vehicles')}
              </Typography>
              <VehiclesTable data={transportVehicles} />
            </TitleTableContainer>
          )}
        </TabContainer>
      ),
    } as dataTabs),
    materialsTableWithTab as dataTabs,
    machineryTableWithTab as dataTabs,
    animalsTableWithTab as dataTabs,
    budgetItemsTableWithTab as dataTabs,
  ] as (dataTabs | false)[]).filter((elem): elem is dataTabs => !!elem);

  // Imágenes unificadas: croquis primero, fotos después (mismo patrón que
  // MultimediaGrid en src/pages/main/workOrders/GeneralView/MultimediaPage).
  // El sketch del loader proviene de GET_IMAGE_BY_ID y en runtime trae
  // storage fields aunque el tipo de la prop es más estrecho.
  const allImages: ImageData[] = [
    ...(sketch
      ? [
          {
            ...(sketch as unknown as ImageData),
            id: sketch.id,
            name: sketch.name || '',
            creatingDate:
              (sketch as unknown as { creatingDate?: string }).creatingDate ??
              sketch.date,
          },
        ]
      : []),
    ...(images ?? []),
  ];

  return (
    <GeneralContainer {...props}>
      <FirstGeneralColumn>
        <BasicInfo>
          <Container>
            <HeaderContainer>
              <Title size="L" variant="semiBold">
                {t('action.summary')}
              </Title>
              {actionStatus && getStatusVariant(actionStatus) && (
                <SpecialTagStyled
                  variant={getStatusVariant(actionStatus)!}
                  sizeTag="default"
                  onClick={onStatusClick}
                />
              )}
            </HeaderContainer>
            <ColumnsContainer>
              <Column>
                <InputRoid
                  label="ROID"
                  roid={
                    typeof roid === 'string'
                      ? roid.split('.')
                      : Array.isArray(roid)
                        ? roid
                        : []
                  }
                  disabled
                  onChangeInputRoid={() => {
                    null;
                  }}
                />
              </Column>
              <Column>
                <Typography size={isLandscape ? 'XS' : 'M'} variant="semiBold">
                  {t('basicInfo')}
                </Typography>
                <Definition
                  title={t('communication.registerDate')}
                  value={date}
                />
                <Definition
                  title={t('communication.registerHour')}
                  value={hour}
                />
                {/* Quién creó el registro y quién lo rellenó. La ficha (ShowOT)
                    ya los enseñaba y este resumen no, y es justo la pantalla
                    donde se valida: hay que poder ver quién metió los datos.
                    Condicionales: en históricos sin operario no se pinta una
                    fila vacía. Mismas etiquetas que la ficha. */}
                {!!creatorName && (
                  <PersonDefinition
                    // La clave trae los dos puntos DENTRO del texto en los tres
                    // idiomas ('Creado por:'), porque su consumidor original es
                    // la etiqueta de un input, que no los añade. <Definition> sí
                    // los añade → salía «Creado por::». Se los quitamos aquí en
                    // vez de tocar los locales (son fuente de verdad de los E2E).
                    title={t('forms:workOrder.createdBy').replace(/:\s*$/, '')}
                    value={creatorName}
                  />
                )}
                {!!registeredByName && (
                  <PersonDefinition
                    title={t('forms:registeredBy.label')}
                    value={registeredByName}
                  />
                )}
              </Column>
            </ColumnsContainer>
          </Container>
          <Container>
            <Typography size={isLandscape ? 'M' : 'L'} variant="semiBold">
              {t('action.locationDescription')}
            </Typography>
            <ColumnsContainer>
              <Column>
                <Definition title={t('user.concession')} value={concession} />
                <Definition title={t('workOrder.road')} value={road} />
                <Definition title={t('workOrder.pkInitial')} value={pkInit} />
                <Definition title={t('workOrder.pkFinal')} value={pkEnd} />
              </Column>
              <Column>
                <Definition title={t('workOrder.roadType')} value={track} />
                {direction && direction !== UNSPECIFIC && (
                  <Definition
                    title={t('workOrder.direction')}
                    value={`${direction === 'both' || !roadDirections[0]
                      ? city
                      : direction === 'down'
                        ? roadDirections[0]
                        : roadDirections[roadDirections.length - 1]
                      } ${directionArrow[direction]}`}
                  />
                )}
                {showMargin && margin && margin !== UNSPECIFIC && (
                  <Definition
                    title={t('workOrder.margin')}
                    value={marginArrow[margin]}
                  />
                )}
                <Definition title={t('workOrder.lane')} value={lane} />
              </Column>
            </ColumnsContainer>
          </Container>
        </BasicInfo>
        {direction && direction !== UNSPECIFIC && mapCenter && (
          <MapContainer>
            <MemorizedMap
              center={mapCenter}
              zoom={15}
              path={path}
              point={mapPoint}
              geoloc={geolocation}
              apiKey={import.meta.env.VITE_MAP_API_KEY}
              // Ampliar y recentrar, como el mapa del recorrido: aquí se LEE
              // dónde se trabajó (no es un formulario).
              allowFullscreen
            />
          </MapContainer>
        )}
        <Container>
          <Typography size={isLandscape ? 'M' : 'L'} variant="semiBold">
            {t('action.actionDescription')}
          </Typography>
          <ContentContainer>
            <ColumnsContainer>
              <Column>
                <Definition title={t('workOrder.chapter')} value={capitol} />
                <Definition
                  title={t('workOrder.subchapter')}
                  value={subCapitol}
                />
                {asset && assetSwitch && (
                  <Definition title={t('workOrder.asset')} value={asset} />
                )}
                {activity && (
                  <Definition title={t('workOrder.action')} value={activity} />
                )}
                <Definition title={t('workOrder.element')} value={element} />
                <Definition
                  title={t('workOrder.laneCut')}
                  value={
                    laneCutting ? t('common:labels.yes') : t('common:labels.no')
                  }
                />
              </Column>
            </ColumnsContainer>
            {/* Bug descripciones (2026-06-05): validada (1ª) + operario (2ª), solo si
                tienen valor; fallback a la combinada legacy si no llegan los nuevos props. */}
            {descriptionValidated && (
              <InputTextArea
                label={t('workOrder.validatedDescription')}
                disabled
                value={descriptionValidated}
              />
            )}
            {operatorDescription && (
              <InputTextArea
                label={t('workOrder.actionDescription')}
                disabled
                value={operatorDescription}
              />
            )}
            {!descriptionValidated && !operatorDescription && description && (
              <InputTextArea
                label={t('workOrder.validatedDescription')}
                disabled
                value={description}
              />
            )}
            {notes && (
              <InputTextArea
                label={t('workOrder.notes')}
                disabled
                value={notes}
              />
            )}
          </ContentContainer>
        </Container>
      </FirstGeneralColumn>
      <SecondGeneralColumn>
        {allImages.length > 0 && (
          <Container>
            <Typography size={isLandscape ? 'M' : 'L'} variant="semiBold">
              {t('actionEdition.images')}
            </Typography>
            <GridOfPhotos imagesToShow={mapToGridImages(allImages, roid)} />
          </Container>
        )}
        {dataTabs.length > 0 && (
          <PergaminoList>
            {dataTabs.map((tab) => (
              <React.Fragment key={tab.label}>{tab.content}</React.Fragment>
            ))}
          </PergaminoList>
        )}
        <ContainerButtons>
          {routes.includes('actions-validation') ? (
            <>
              {rightButton && (
                <MainButton
                  type="button"
                  text={rightButton}
                  onClick={onContinueAction}
                />
              )}
              {leftButton &&
                (!routes.includes('actions-validation') ||
                  !isActionEditedBoolean) && (
                  <MainButton
                    type="button"
                    variant="secondary"
                    text={leftButton}
                    onClick={onEditAction}
                  />
                )}
              {showValidateButton && validateButton && (
                <MainButton
                  type="button"
                  text={validateButton}
                  onClick={onValidateAction}
                  disabled={isValidating}
                />
              )}
              {showPendingCertificationButton && pendingCertificationButton && (
                <MainButton
                  type="button"
                  text={pendingCertificationButton}
                  onClick={onPendingCertificationAction}
                  disabled={isMarkingPendingCertification}
                />
              )}
            </>
          ) : (
            <>
              {leftButton &&
                (!routes.includes('actions-validation') ||
                  !isActionEditedBoolean) && (
                  <MainButton
                    type="button"
                    variant="secondary"
                    text={leftButton}
                    onClick={onEditAction}
                  />
                )}
              {rightButton && (
                <MainButton
                  type="button"
                  text={rightButton}
                  onClick={onContinueAction}
                />
              )}
            </>
          )}
          {/* {leftButton &&
            (!routes.includes('actions-validation') ||
              !isActionEditedBoolean) && (
              <MainButton
                type="button"
                variant="secondary"
                text={leftButton}
                onClick={onEditAction}
              />
            )}
          {rightButton && (
            <MainButton
              type="button"
              text={rightButton}
              onClick={onContinueAction}
            />
          )} */}
        </ContainerButtons>
      </SecondGeneralColumn>
    </GeneralContainer>
  );
}
